/* global Office */
import { OutlookItemNotSupported } from 'common/exceptions/outlook/OutlookItemNotSupported';
import { AddInStakeholder } from '../models/AddInStakeholder';
import { SupportedOfficeItem } from './SupportedOfficeItem';
import { getAsyncOrganizer, getAsyncRecipients } from './outlookAsyncUtils';
import {
  getUserEmail,
  isAppointmentReadMode,
  isMessageSentByUser,
  isSameEmail,
} from './outlookItemUtils';

export function getAllPartiesFromMessage(
  outlookItem: Office.MessageRead,
  includeCc = true
) {
  let addresses: Office.EmailAddressDetails[] = [];

  const message = outlookItem as Office.MessageRead;
  if (outlookItem.from.emailAddress) {
    addresses.push(outlookItem.from);
  }
  addresses.push(...message.to);

  if (includeCc) {
    addresses.push(...message.cc);
  }

  // TODO: in read mode there is no bcc?
  //if (outlookItem.bcc) {
  //addresses.push(...await getRecipients(outlookItem.bcc));
  //}

  addresses = removeDuplicatesByEmail(addresses);
  return addresses;
}

export async function getAllPartiesFromAppointment(
  appointment: Office.AppointmentRead & Office.AppointmentCompose
) {
  let addresses: Office.EmailAddressDetails[] = [];

  if (isAppointmentReadMode(appointment)) {
    const appointmentRead = appointment as Office.AppointmentRead;

    addresses.push(...appointmentRead.requiredAttendees);
    addresses.push(...appointmentRead.optionalAttendees);
  } else {
    const appointmentCompose = appointment as Office.AppointmentCompose;

    addresses.push(
      ...(await getAsyncRecipients(appointmentCompose.requiredAttendees))
    );
    addresses.push(
      ...(await getAsyncRecipients(appointmentCompose.optionalAttendees))
    );

    // On Outlook desktop, all Appointments to which user didn't add any attendees,
    // will still have appointment organizer in required attendees list.
    // On Outlook Online it isn't the case, and newly created Appointment will not have any attendees.
    // We'll solve this by adding the organizer ourselves.
    if (addresses.length === 0 && appointmentCompose.organizer) {
      const organizer = await getAsyncOrganizer(appointmentCompose.organizer);
      addresses.push(organizer);
    }
  }

  addresses = removeDuplicatesByEmail(addresses);
  return addresses;
}

export async function getPrimaryStakeholders(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined
): Promise<AddInStakeholder[]> {
  const primaryStakeholders: AddInStakeholder[] = [];

  if (item.itemType === 'message') {
    const all = await getAllStakeholders(item, sharedProperties);
    if (all && all.length > 0) primaryStakeholders.push(all[0]);
  } else if (item.itemType === 'appointment') {
    const all = await getAllParties(item);
    primaryStakeholders.push(...all);
  } else {
    throw new OutlookItemNotSupported();
  }

  if (!primaryStakeholders) {
    primaryStakeholders.push({
      email: 'No stakeholders',
      fullName: 'No stakeholders',
      ...getFirstAndLastName(''),
    });
  }

  return primaryStakeholders;
}

export async function getAdditionalStakeholders(
  officeItem: Office.MessageRead &
    Office.AppointmentRead &
    Office.AppointmentCompose,
  sharedProperties: Office.SharedProperties | undefined
) {
  if (officeItem.itemType === 'appointment') {
    return [];
  }
  const stakeholders = await getAllStakeholders(officeItem, sharedProperties);
  return stakeholders.slice(1);
}

export async function getAllStakeholders(
  officeItem: Office.MessageRead &
    Office.AppointmentRead &
    Office.AppointmentCompose,
  sharedProperties: Office.SharedProperties | undefined,
  includeCc = true
) {
  const userEmail = getUserEmail(sharedProperties);

  let parties = await getAllParties(officeItem, includeCc);
  if (parties.length > 1) {
    // If the user sent the message from an alias, the From address is also the user
    const senderEmail =
      officeItem.itemType === 'message' &&
      (await isMessageSentByUser(officeItem, sharedProperties))
        ? officeItem.from.emailAddress
        : undefined;

    parties = parties.filter(
      (a) =>
        !isSameEmail(a.email, userEmail) && !isSameEmail(a.email, senderEmail)
    );
  }
  return parties;
}

async function getAllParties(
  officeItem: Office.MessageRead &
    Office.AppointmentRead &
    Office.AppointmentCompose,
  includeCc = true
) {
  let addresses: Office.EmailAddressDetails[];
  if (officeItem.itemType === 'message')
    addresses = getAllPartiesFromMessage(
      officeItem as Office.MessageRead,
      includeCc
    );
  else if (officeItem.itemType === 'appointment')
    addresses = await getAllPartiesFromAppointment(
      officeItem as Office.AppointmentRead & Office.AppointmentCompose
    );
  else {
    throw new OutlookItemNotSupported();
  }

  const parties = addresses.map((a) => {
    return {
      email: a.emailAddress,
      fullName: a.displayName,
      ...getFirstAndLastName(a.displayName),
    };
  });
  return parties;
}

function getFirstAndLastName(displayName: string) {
  //display name split code
  //  var str=item.from.displayName
  let firstName = '';
  let lastName = '';
  if (displayName.split(' ').length > 1) {
    const index = displayName.indexOf(' '); // Gets the first index where a space occurs
    firstName = displayName.substr(0, index); // Gets the first part
    lastName = displayName.substr(index + 1); // Gets the text part
  } else {
    firstName = displayName;
    lastName = '';
  }
  return { firstName: firstName, lastName: lastName };
}

function removeDuplicatesByEmail(addresses: Office.EmailAddressDetails[]) {
  addresses.forEach((a, i) => {
    const duplicates = addresses.filter(
      (d) => d.emailAddress === a.emailAddress && d != a
    );
    if (duplicates.length > 0) addresses.splice(i, 1);
  });
  return addresses;
}
